import { Controller } from '@hotwired/stimulus'

/**
 * Stimulus-контроллер для формы отзыва.
 *
 * Управляет раскрытием поля "Другое": синхронизирует видимость блока,
 * ARIA-атрибуты, текст кнопки и поворот иконки переключателя.
 *
 * @extends {Controller}
 */
export default class ReviewFormController extends Controller {
  /**
   * Кнопка переключения и блок дополнительного контента.
   *
   * @type {string[]}
   */
  static targets = ['otherToggle', 'otherContent']

  /**
   * Синхронизирует состояние дополнительного блока после подключения
   * контроллера к DOM.
   *
   * @returns {void}
   */
  connect() {
    this.syncOtherContentState()
  }

  /**
   * Обрабатывает клик по переключателю дополнительного блока.
   *
   * @param {Event} event - Событие активации переключателя.
   * @returns {void}
   */
  toggleOtherContent(event) {
    event.preventDefault()

    this.setOtherContentExpanded(
      this.otherContentTarget.classList.contains('hidden')
    )
  }

  /**
   * Восстанавливает состояние дополнительного блока из текущей разметки.
   *
   * @returns {void}
   */
  syncOtherContentState() {
    this.setOtherContentExpanded(
      !this.otherContentTarget.classList.contains('hidden')
    )
  }

  /**
   * Переключает видимость дополнительного блока и обновляет связанные
   * атрибуты доступности.
   *
   * @param {boolean} isExpanded - Нужно ли показать дополнительный блок.
   * @returns {void}
   */
  setOtherContentExpanded(isExpanded) {
    this.otherContentTarget.classList.toggle('hidden', !isExpanded)
    this.otherContentTarget.setAttribute('aria-hidden', String(!isExpanded))

    if (this.hasOtherToggleTarget) {
      this.otherToggleTarget.setAttribute('aria-expanded', String(isExpanded))
      this.updateOtherToggleText(isExpanded)
      this.rotateOtherToggleIcon(isExpanded)
    }
  }

  /**
   * Обновляет текст переключателя по значениям из `data-expanded-text` и
   * `data-collapsed-text`.
   *
   * @param {boolean} isExpanded - Текущее раскрытое состояние блока.
   * @returns {void}
   */
  updateOtherToggleText(isExpanded) {
    const text = isExpanded
      ? this.otherToggleTarget.dataset.expandedText
      : this.otherToggleTarget.dataset.collapsedText

    if (!text) return

    const textElement =
      this.otherToggleTarget.querySelector('.link__text') ||
      this.otherToggleTarget

    textElement.textContent = text
  }

  /**
   * Поворачивает последнюю SVG-иконку внутри переключателя в зависимости от
   * состояния блока.
   *
   * @param {boolean} isExpanded - Текущее раскрытое состояние блока.
   * @returns {void}
   */
  rotateOtherToggleIcon(isExpanded) {
    const icons = this.otherToggleTarget.querySelectorAll('svg')
    const icon = icons[icons.length - 1]

    if (!icon) return

    icon.style.transform = isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
    icon.style.transformOrigin = 'center'
  }
}
